import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import PasswordReset from './PasswordReset';

const CognitoLogin: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [passwordChallenge, setPasswordChallenge] = useState<{
    email: string;
    session: string;
  } | null>(null);
  const [newPasswordData, setNewPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const { login, completePasswordChallenge } = useCognitoAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPasswordData({
      ...newPasswordData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('CognitoLogin: Starting login with:', formData.email);
      await login(formData);
      console.log('CognitoLogin: Login successful, scheduling navigation');
      // Use setTimeout to ensure state has been updated before navigation
      setTimeout(() => {
        console.log('CognitoLogin: Navigating to dashboard');
        navigate('/dashboard');
      }, 0);
    } catch (error: any) {
      console.error('Login error:', error);

      // Check for NEW_PASSWORD_REQUIRED challenge
      if (error.code === 'NEW_PASSWORD_REQUIRED') {
        console.log('CognitoLogin: Password challenge detected, session:', error.session);
        setPasswordChallenge({
          email: error.email || formData.email,
          session: error.session,
        });
        setLoading(false);
        return;
      }

      // Check for password change requirement from Cognito API (legacy)
      if (error.response?.status === 400 && error.response?.data?.requiresPasswordChange) {
        setPasswordChallenge({
          email: formData.email,
          session: error.response.data.session || '',
        });
        setLoading(false);
        return;
      }

      if (error.message?.includes('NotAuthorizedException')) {
        setError('Invalid email or password');
      } else if (error.message?.includes('UserNotConfirmedException')) {
        setError('Account not confirmed. Please check your email.');
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Login failed. Please try again.');
      }
      setLoading(false);
    } finally {
      // Don't set loading to false here, we already do it in error handling
    }
  };

  const handlePasswordChallengeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (newPasswordData.newPassword !== newPasswordData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPasswordData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (!passwordChallenge) {
      setError('Invalid state');
      return;
    }

    setLoading(true);

    try {
      console.log('CognitoLogin: Responding to password challenge');
      await completePasswordChallenge({
        email: passwordChallenge.email,
        session: passwordChallenge.session,
        newPassword: newPasswordData.newPassword,
      });
      console.log('CognitoLogin: Password challenge completed, navigating to dashboard');
      // Use setTimeout to ensure state has been updated before navigation
      setTimeout(() => {
        navigate('/dashboard');
      }, 0);
    } catch (error: any) {
      console.error('CognitoLogin: Password challenge failed:', error);
      setError(error.response?.data?.error || 'Password change failed');
    } finally {
      setLoading(false);
    }
  };

  // Show password reset form if requested
  if (showPasswordReset) {
    return (
      <PasswordReset
        onBack={() => setShowPasswordReset(false)}
        initialEmail={formData.email}
      />
    );
  }

  // Show password challenge form if password change is required
  if (passwordChallenge) {
    return (
      <div className="login-container">
        <div className="login-form">
          <h2>Set Your Password</h2>
          <p>This is your first login. Please set a permanent password.</p>
          <p className="info-text">Email: {passwordChallenge.email}</p>
          
          {error && <div className="error-message">{error}</div>}
          
          <form onSubmit={handlePasswordChallengeSubmit}>
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={newPasswordData.newPassword}
                onChange={handleNewPasswordChange}
                required
                disabled={loading}
                placeholder="Enter your new password"
                minLength={8}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={newPasswordData.confirmPassword}
                onChange={handleNewPasswordChange}
                required
                disabled={loading}
                placeholder="Confirm your new password"
                minLength={8}
              />
            </div>
            
            <button type="submit" disabled={loading} className="submit-button">
              {loading ? 'Setting Password...' : 'Set Password'}
            </button>
          </form>
          
          <button 
            type="button" 
            onClick={() => setPasswordChallenge(null)} 
            className="back-button"
            disabled={loading}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-form">
        <h2>Welcome to SyncMySquad</h2>
        <p>Sign in with your invitation credentials</p>

        {error && (
          <div className="error-container">
            <div className="error-message">{error}</div>
            {(error.includes('Login failed') || error.includes('Invalid')) && (
              <div className="reset-password-prompt">
                <button
                  type="button"
                  onClick={() => setShowPasswordReset(true)}
                  className="reset-password-link"
                  disabled={loading}
                >
                  Reset Password?
                </button>
              </div>
            )}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>
          
          <button type="submit" disabled={loading} className="submit-button">
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        
        <div className="auth-info">
          <p><strong>New to SyncMySquad?</strong></p>
          <p>Access is by invitation only. If you received an invitation email, use those credentials to sign in.</p>
          <p>If you need access, please contact an administrator.</p>
        </div>
      </div>
    </div>
  );
};

export default CognitoLogin;